<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useManager } from 'dashboard/composables/useManager';
import { useConversationLabels } from 'dashboard/composables/useConversationLabels';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useLabelVisibility } from 'dashboard/composables/useLabelVisibility';
import Spinner from 'shared/components/Spinner.vue';
import LabelDropdown from 'shared/components/ui/label/LabelDropdown.vue';
import AddLabel from 'shared/components/ui/dropdown/AddLabel.vue';

const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();
const { isManager, isManagerOrAdmin } = useManager();

const currentChat = useMapGetter('getSelectedChat');
const conversationUiFlags = useMapGetter('conversationLabels/getUIFlags');
const {
  savedLabels,
  activeLabels,
  addLabelToConversation,
  removeLabelFromConversation,
} = useConversationLabels();
const { myTeams, visibleLabels } = useLabelVisibility();

const showSearchDropdownLabel = ref(false);

const allowCreation = computed(
  () => (isAdmin.value || isManager.value) && myTeams.value.length > 0
);
const showTeamRequiredNote = computed(
  () => isManagerOrAdmin.value && myTeams.value.length === 0
);
const visibleLabelTitles = computed(
  () => new Set(visibleLabels.value.map(label => label.title))
);
const visibleActiveLabels = computed(() =>
  activeLabels.value.filter(label => visibleLabelTitles.value.has(label.title))
);

const toggleLabels = () => {
  showSearchDropdownLabel.value = !showSearchDropdownLabel.value;
};

const closeDropdownLabel = () => {
  showSearchDropdownLabel.value = false;
};

const createAndAssignLabel = async label => {
  try {
    await store.dispatch('conversationLabels/createAndAssignLabel', {
      conversationId: currentChat.value.id,
      label,
    });
    useAlert(
      t('CONTACT_PANEL.LABELS.LABEL_SELECT.CREATE_SUCCESS', {
        label: label.title,
      })
    );
    closeDropdownLabel();
  } catch (error) {
    const errorMessage = error?.response?.data?.message;
    useAlert(
      errorMessage || t('CONTACT_PANEL.LABELS.LABEL_SELECT.CREATE_FAILED')
    );
  }
};

useKeyboardEvents({
  KeyL: {
    action: event => {
      event.preventDefault();
      toggleLabels();
    },
  },
  Escape: {
    action: () => {
      if (showSearchDropdownLabel.value) toggleLabels();
    },
    allowOnFocusedInput: true,
  },
});
</script>

<template>
  <div class="mb-0">
    <div v-if="!conversationUiFlags.isFetching" class="w-full">
      <div
        v-on-clickaway="closeDropdownLabel"
        class="relative flex flex-wrap leading-6"
        @keyup.esc="closeDropdownLabel"
      >
        <AddLabel @add="toggleLabels" />
        <woot-label
          v-for="label in visibleActiveLabels"
          :key="label.id"
          :title="label.title"
          :description="label.description"
          show-close
          :color="label.color"
          variant="smooth"
          class="max-w-[calc(100%-0.5rem)]"
          @remove="removeLabelFromConversation"
        />

        <div
          class="absolute top-6 z-[9999] w-full rounded-lg border border-n-strong bg-n-alpha-3 p-2 shadow-lg backdrop-blur-[100px] dark:border-n-strong"
          :class="{
            'block visible': showSearchDropdownLabel,
            'hidden invisible': !showSearchDropdownLabel,
          }"
        >
          <LabelDropdown
            v-if="showSearchDropdownLabel"
            :account-labels="visibleLabels"
            :selected-labels="savedLabels"
            :allow-creation="allowCreation"
            :creation-teams="myTeams"
            :is-creating="conversationUiFlags.isUpdating"
            @add="addLabelToConversation"
            @remove="removeLabelFromConversation"
            @create="createAndAssignLabel"
          />
          <p
            v-if="showSearchDropdownLabel && showTeamRequiredNote"
            data-testid="team-required-note"
            class="m-0 border-t border-n-weak px-2 pt-2 text-xs text-n-slate-11"
          >
            {{ $t('CONTACT_PANEL.LABELS.LABEL_SELECT.TEAM_REQUIRED') }}
          </p>
        </div>
      </div>
    </div>
    <Spinner v-else />
  </div>
</template>
